import { ReactNode, useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useSnackBar } from "@/utils/useSnackbar";

import { AssignStatus, InclusionFilterStatus, SelectType } from "@/enum/list.enum";

import { useOutreachActivation } from "@/components/OutreachActivation/useOutreachActivation";
import { useBlacklistOverrideGate } from "@/components/BlacklistOverride/useBlacklistOverrideGate";

import { Sender } from "@/pages/Private/pages/List/components/CampaignOverview/components/Sender";
import { resolveLeadReviewTarget } from "@/pages/Private/pages/List/utils/resolveLeadReviewTarget";
import { buildReadyToSendListLink } from "@/pages/Private/pages/List/utils/buildReadyToSendListLink";
import { useSenderConfirmGate } from "@/pages/Private/pages/List/components/CampaignOverview/utils/useSenderConfirmGate";
import {
	campaignSendersFor,
	toSenderIdentities,
} from "@/pages/Private/pages/Messages/utils/campaignSenders";

import {
	useBulkAssignMutation,
	useBulkRunPromptMutation,
	useUpdateListPersonMutation,
	useUpdateListPersonSendersMutation,
} from "@/pages/Private/redux/list/list.api";

import { PickerCampaign } from "./reachOutCampaigns";
import {
	isPageOnlyOverride,
	narrowReachOutTarget,
	reachOutBulkScope,
	ReachOutTarget,
	reachOutTargetLeads,
	reachOutTargetListId,
} from "./reachOutTarget";

interface UseReachOutCampaignCommitParams {
	target: ReachOutTarget;
	selected: PickerCampaign | null;
	effectiveSenders: Sender[];
	changeSenders: boolean;
	/** Close the whole reach-out menu after an action commits. */
	onDone: () => void;
	/** "Don't add the N connected leads" (PREC-4426): bulkAssign leaves out leads connected to these. */
	skipConnectedToUserIds?: number[];
}

interface ReachOutCampaignCommit {
	busy: boolean;
	/**
	 * "Add to campaign" — assign & send out (assignStatus ASSIGNING). Asks first when the campaign is
	 * not sending (PREC-3296); the pre-assign actions below send nothing and never ask.
	 */
	handleAddAndSend: () => Promise<void>;
	/** "Review & edit campaign" — pre-assign for review (campaignId + addedToCampaignAt, no send). */
	handleAssignToCampaign: () => Promise<void>;
	/** "Create AI snippets" — pre-assign, then generate the campaign's snippet columns. */
	handleCreateSnippets: () => Promise<void>;
	/** The "start this campaign?" prompt; render it beside the picker. */
	activationModal: ReactNode;
	/** The "contact these blacklisted leads?" prompt (PREC-2548); render it beside the picker. */
	overrideModal: ReactNode;
	/** The "send as these colleagues?" question (PREC-4312); render it beside the picker. */
	senderConfirmModal: ReactNode;
}

/**
 * Owns every terminal mutation of the reach-out campaign picker for both a single lead and a bulk
 * table selection. The per-lead path mirrors the ReadyToSend assign modal exactly (`bulkAssign` on
 * one id / `updateListPerson` for review); the bulk path reuses the same endpoints over the whole
 * selection — passing `selectType` + `filter` so "select all across pages" is honored, and applying
 * a sender override through `bulkAssign`'s `senders` field (one call) rather than per lead.
 *
 * "Review & edit campaign" in bulk deliberately assigns with `AssignStatus.DRAFT`: `reassign()` only
 * schedules a send when the status is `ASSIGNING`, but always sets `campaignId` + `addedToCampaignAt`
 * for a campaign assign — so DRAFT yields the same "pre-assigned, awaiting review" state the per-lead
 * `updateListPerson` produces, with no send.
 *
 * Both actions that put leads into a campaign go through the blacklist gate first (PREC-2548): a
 * blacklisted lead is contacted only once the user confirmed taking it off the blacklist, and the
 * commit then runs on the leads the gate let through — the list table's bulk Reach out and the
 * lead panel's "Add to → Campaign" both land here, so neither can skip it.
 */
export const useReachOutCampaignCommit = ({
	target,
	selected,
	effectiveSenders,
	changeSenders,
	onDone,
	skipConnectedToUserIds,
}: UseReachOutCampaignCommitParams): ReachOutCampaignCommit => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { handleOpenSnackbar } = useSnackBar();

	const [busy, setBusy] = useState(false);

	const [bulkAssign] = useBulkAssignMutation();
	const [updateListPerson] = useUpdateListPersonMutation();
	const [updateListPersonSenders] = useUpdateListPersonSendersMutation();
	const [bulkRunPrompt] = useBulkRunPromptMutation();
	const { assignWithActivation, activationModal } = useOutreachActivation();
	const { gate, overrideModal } = useBlacklistOverrideGate();
	const { confirmSenders, senderConfirmModal } = useSenderConfirmGate();

	const listId = reachOutTargetListId(target);

	// Seed for a bulk `bulkAssign`: the current-page ids the backend uses to resolve the list (and,
	// for a CURRENT_PAGE selection, the exact set to assign); `assign[0].campaignId` is applied to
	// every resolved lead for an ALL selection.
	const bulkAssignSeed = (gated: ReachOutTarget, campaignId: string) =>
		gated.kind === "bulk"
			? gated.selectedItems.map((id) => ({ listPersonId: id, campaignId }))
			: [];

	// Who "Add to campaign" sends as, in the API's order (campaignSendersFor): each loaded lead's, and
	// for a select-all also the leads not loaded, which take the override or the campaign's senders.
	const sendsAs = (campaign: PickerCampaign) => {
		const override = changeSenders ? effectiveSenders : undefined;
		const loaded = reachOutTargetLeads(target).flatMap((row) =>
			campaignSendersFor({ override, prospect: row, campaign })
		);
		const rest = target.kind === "bulk" ? campaignSendersFor({ override, campaign }) : [];

		return toSenderIdentities([...loaded, ...rest]);
	};

	const bulkSenders = (gated: ReachOutTarget) =>
		gated.kind === "bulk" && changeSenders ? JSON.stringify(effectiveSenders) : undefined;

	/**
	 * Runs a commit once the blacklist gate let its leads through, on the target narrowed to them.
	 * Answers what the commit answered, or nothing when the user cancelled or nobody may be
	 * contacted — the menu then stays open with nothing written.
	 */
	const withBlacklistGate = async <R>(
		commit: (gated: ReachOutTarget) => Promise<R>
	): Promise<R | undefined> => {
		const outcome: { value?: R } = {};
		const note = isPageOnlyOverride(target)
			? t("blacklist.override.pageOnlyNote", { total: target.count })
			: undefined;

		await gate(
			reachOutTargetLeads(target),
			async (allowed) => {
				outcome.value = await commit(narrowReachOutTarget(target, allowed));
			},
			{ note }
		);

		return outcome.value;
	};

	// The send-out assign itself, for a single lead or the bulk selection.
	const sendOut = async (campaign: PickerCampaign, gated: ReachOutTarget) => {
		if (gated.kind === "lead") {
			if (changeSenders) {
				await updateListPersonSenders({ id: gated.row.id, senders: effectiveSenders }).unwrap();
			}

			await bulkAssign({
				listId,
				assign: [{ listPersonId: gated.row.id, campaignId: campaign.id }],
				assignStatus: AssignStatus.ASSIGNING,
				selectType: SelectType.CURRENT_PAGE,
				inclusionFilterStatus: [InclusionFilterStatus.ADDED],
			}).unwrap();

			handleOpenSnackbar(
				t("list.reachOut.toast.assigned", {
					name: gated.row.prospect?.firstName ?? gated.row.prospect?.name ?? "",
					campaign: campaign.name,
				})
			);

			return;
		}

		const result = await bulkAssign({
			listId,
			assign: bulkAssignSeed(gated, campaign.id),
			assignStatus: AssignStatus.ASSIGNING,
			...reachOutBulkScope(gated),
			senders: bulkSenders(gated),
			skipConnectedToUserIds,
		}).unwrap();

		// A refused or failed lookup answers "failed" with nothing added: never report it as added.
		if (result?.status === "failed") {
			throw new Error("bulkAssign failed");
		}

		// What the server added, not the selection's size (root CLAUDE.md, PREC-4595).
		handleOpenSnackbar(
			t(
				result?.skippedConnected
					? "list.reachOut.toast.assignedBulkSkipped"
					: "list.reachOut.toast.assignedBulk",
				{
					count: result?.assigned ?? gated.count,
					skipped: result?.skippedConnected,
					campaign: campaign.name,
				}
			)
		);
	};

	const handleAddAndSend = async () => {
		if (!selected || busy) {
			return;
		}

		setBusy(true);

		try {
			// Sending as colleagues is asked first, once, before anything is written (PREC-4312).
			if (!(await confirmSenders(sendsAs(selected), 2))) {
				return;
			}

			// Starting is per campaign and asked for (PREC-3296). This used to set the list's own
			// campaignStatus to RUNNING without a word — which starts every non-paused campaign on the
			// list, not just the one picked here. A cancel leaves the menu open with nothing written.
			const assigned = await withBlacklistGate((gated) =>
				assignWithActivation({
					listId,
					campaignIds: [selected.id],
					assign: () => sendOut(selected, gated),
				})
			);

			if (assigned) {
				onDone();
			}
		} catch {
			handleOpenSnackbar(t("inbox.runAiSnippetsError"));
		} finally {
			setBusy(false);
		}
	};

	// "Review & edit campaign" — pre-assign without sending, then open the review surface.
	const assignForReview = async (campaign: PickerCampaign, gated: ReachOutTarget) => {
		if (gated.kind === "lead") {
			if (changeSenders) {
				await updateListPersonSenders({ id: gated.row.id, senders: effectiveSenders }).unwrap();
			}

			await updateListPerson({
				id: gated.row.id,
				campaignId: campaign.id,
				addedToCampaignAt: new Date(),
			}).unwrap();

			// Where the lead is reviewed depends on the lead, not on the fact that it was just assigned:
			// a DRAFT lead lands on Review-to-send, one the campaign already owns on the Outbox, one that
			// has replied in the Inbox (PREC-4103).
			const reviewLink = resolveLeadReviewTarget(gated.row);

			if (reviewLink) {
				navigate(reviewLink);
			}

			return true;
		}

		// Pre-assign for review with DRAFT — sets campaignId + addedToCampaignAt without sending.
		// `createReviewSession` freezes the assigned selection server-side so Review shows exactly
		// these leads instead of paging through the whole list (PREC-3648). If the session couldn't
		// be created the response has no id and the link falls back to the list-wide view.
		const bulkAssignResult = await bulkAssign({
			listId,
			assign: bulkAssignSeed(gated, campaign.id),
			assignStatus: AssignStatus.DRAFT,
			...reachOutBulkScope(gated),
			senders: bulkSenders(gated),
			skipConnectedToUserIds,
			createReviewSession: true,
		}).unwrap();

		navigate(buildReadyToSendListLink(gated.list, bulkAssignResult?.reviewSessionId));

		return true;
	};

	const handleAssignToCampaign = async () => {
		if (!selected || busy) {
			return;
		}

		setBusy(true);

		try {
			const assigned = await withBlacklistGate((gated) => assignForReview(selected, gated));

			if (assigned) {
				onDone();
			}
		} catch {
			handleOpenSnackbar(t("inbox.runAiSnippetsError"));
		} finally {
			setBusy(false);
		}
	};

	const handleCreateSnippets = async () => {
		if (!selected || busy) {
			return;
		}

		setBusy(true);

		try {
			if (target.kind === "lead") {
				// Pre-assign so Review shows the right campaign, then generate the snippet-column values.
				await updateListPerson({ id: target.row.id, campaignId: selected.id }).unwrap();
				await bulkRunPrompt({ ids: [target.row.id], listId }).unwrap();
			} else {
				await bulkAssign({
					listId,
					assign: bulkAssignSeed(target, selected.id),
					assignStatus: AssignStatus.DRAFT,
					...reachOutBulkScope(target),
				}).unwrap();

				await bulkRunPrompt({
					ids: target.selectedItems,
					listId,
					assignStatus: target.assignStatus,
					...reachOutBulkScope(target),
				}).unwrap();
			}

			handleOpenSnackbar(t("list.reachOut.toast.snippets", { campaign: selected.name }));
			onDone();
		} catch {
			handleOpenSnackbar(t("inbox.runAiSnippetsError"));
		} finally {
			setBusy(false);
		}
	};

	return {
		busy,
		handleAddAndSend,
		handleAssignToCampaign,
		handleCreateSnippets,
		activationModal,
		overrideModal,
		senderConfirmModal,
	};
};
